import { Controller } from "@hotwired/stimulus"
import BaseGrid, { html, Handlebars } from "../utils/base_grid"

// Pre-compile templates
const typeBadgeTemplate = Handlebars.compile(`
  <span class="badge badge-type">{{type}}</span>
`)

const booksTemplate = Handlebars.compile(`
  {{#each books}}
    <span class="badge badge-secondary" data-bs-toggle="tooltip" data-bs-title="{{name}}">{{name}}</span>
  {{/each}}
`)

const statusBadgeTemplate = Handlebars.compile(`
  {{#if (eq status "translated")}}
    <span class="badge badge-success">✓ {{translatedLabel}}</span>
    <br><small class="text-muted">{{updatedLabel}} {{updatedAt}}</small>
  {{else if (eq status "outdated")}}
    <span class="badge badge-success">✓ {{translatedLabel}}</span>
    <span class="badge badge-warning" data-bs-toggle="tooltip" data-bs-title="Source updated after translation">⚠️ {{outdatedLabel}}</span>
    <br><small class="text-muted">{{updatedLabel}} {{updatedAt}}</small>
  {{else}}
    <span class="badge badge-warning">{{missingLabel}}</span>
  {{/if}}
`)

const actionButtonsTemplate = Handlebars.compile(`
  <div class="btn-group">
    <a href="{{viewUrl}}" class="btn btn-icon btn-sm" data-turbo-frame="_top" data-bs-toggle="tooltip" data-bs-title="{{viewTitle}}">
      <i class="fas fa-eye"></i>
    </a>
    <a href="{{editUrl}}" class="btn btn-icon btn-sm btn-primary" data-turbo-frame="_top" data-bs-toggle="tooltip" data-bs-title="{{editTitle}}">
      {{#if (eq status "missing")}}
        <i class="fas fa-plus"></i>
      {{else}}
        <i class="fas fa-edit"></i>
      {{/if}}
    </a>
  </div>
`)

Handlebars.registerHelper('eq', function(a, b) {
  return a === b;
});

export default class extends Controller {
  static values = { 
    url: String,
    translations: Object
  }
  static targets = ["container", "skeleton", "searchInput", "statusFilter", "typeFilter"]

  connect() {
    // Get translations with defaults
    const t = this.translationsValue || {}
    const labels = {
      name: t.name || "Name",
      type: t.type || "Type",
      books: t.books || "Book(s)",
      status: t.status || "Translation Status",
      actions: t.actions || "Actions",
      view: t.view || "View",
      edit: t.edit || "Edit",
      create: t.create || "Create",
      translated: t.translated || "Translated",
      missing: t.missing || "Missing",
      outdated: t.outdated || "Outdated",
      updated: t.updated || "Updated"
    }
    
    // Store for later use
    this.typeTranslations = t.types || {}
    this.labels = labels

    this.baseGrid = new BaseGrid(this, {
      limit: 25,
      columns: [
        { 
          name: labels.name, 
          id: "name",
          formatter: (cell, row) => {
            const link = row.cells[5].data
            if (link) {
              return html(`<strong>${cell}</strong><br><code class="small">${link}</code>`)
            }
            return html(`<strong>${cell}</strong>`)
          }
        },
        { 
          name: labels.type, 
          id: "type",
          formatter: (cell) => {
            const translatedType = this.typeTranslations[cell] || cell
            return html(typeBadgeTemplate({ type: translatedType }))
          }
        },
        { 
          name: labels.books, 
          id: "books",
          formatter: (cell) => html(booksTemplate({ books: cell }))
        },
        { 
          name: labels.status, 
          id: "translation_status",
          formatter: (cell, row) => {
            const updatedAt = row.cells[6].data
            return html(statusBadgeTemplate({ 
              status: cell, 
              updatedAt,
              translatedLabel: this.labels.translated,
              missingLabel: this.labels.missing,
              outdatedLabel: this.labels.outdated,
              updatedLabel: this.labels.updated
            }))
          }
        },
        { name: "ID", id: "id", hidden: true },
        { name: "Link", id: "link", hidden: true },
        { name: "TranslationUpdated", id: "translation_updated_at", hidden: true },
        { name: "ViewURL", id: "view_url", hidden: true },
        { name: "EditURL", id: "edit_url", hidden: true },
        { 
          name: labels.actions,
          sort: false,
          formatter: (cell, row) => {
             const status = row.cells[3].data
             const viewUrl = row.cells[7].data
             const editUrl = row.cells[8].data
             return html(actionButtonsTemplate({ 
               viewUrl, 
               editUrl, 
               status,
               viewTitle: this.labels.view,
               editTitle: status === 'missing' ? this.labels.create : this.labels.edit
             })) 
          }
        }
      ],
      mapData: (item) => [
        item.name, 
        item.type, 
        item.books,
        item.translation_status,
        item.id,
        item.link,
        item.translation_updated_at,
        item.view_url,
        item.edit_url
      ]
    })

    this.grid = this.baseGrid.create()
  }

  disconnect() {
    this.baseGrid.destroy()
  }

  search(event) {
    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => {
      const query = event.target.value.trim()
      this.baseGrid.updateWithFilters({ q: query })
    }, 300)
  }

  filterByStatus(event) {
    const filter = event.target.value
    this.baseGrid.updateWithFilters({ filter })
  }

  filterByType(event) {
    const type = event.target.value
    this.baseGrid.updateWithFilters({ type })
  }
}
